import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Monitor, Smartphone, Trash2, ShieldCheck, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { authApi } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';

interface Session {
  sid:        string;
  isCurrent:  boolean;
  expiresAt:  string | null;
  ip:         string;
  ua:         string;
  createdAt:  string | null;
  lastActive: string | null;
}

function parseDevice(ua: string): { label: string; isMobile: boolean } {
  const lower = ua.toLowerCase();
  const isMobile = /mobile|android|iphone|ipad/.test(lower);
  if (/chrome/.test(lower))  return { label: 'Chrome', isMobile };
  if (/firefox/.test(lower)) return { label: 'Firefox', isMobile };
  if (/safari/.test(lower))  return { label: 'Safari', isMobile };
  if (/edge/.test(lower))    return { label: 'Edge', isMobile };
  return { label: 'Browser', isMobile };
}

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function SessionsPage() {
  const queryClient    = useQueryClient();
  const { user }       = useAuthStore();
  const [revoking, setRevoking] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn:  () => authApi.sessions().then(r => r.data.sessions as Session[]),
    staleTime: 0,
  });

  const revoke = useMutation({
    mutationFn: (sid: string) => authApi.revokeSession(sid),
    onMutate:   (sid) => setRevoking(sid),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
    onSettled:  () => setRevoking(null),
  });

  const sessions = data ?? [];

  return (
    <div className="p-8 space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-navy">Active Sessions</h1>
        <p className="text-gray-400 text-sm mt-0.5">
          Devices where your account is currently signed in. Revoking a session signs out that device immediately.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-start gap-3">
        <ShieldCheck size={16} className="text-clinical-info mt-0.5 flex-shrink-0"/>
        <p className="text-xs text-gray-600">
          Maximum <strong>3 concurrent sessions</strong> allowed. New logins automatically evict the oldest session.
          If you see a session you don't recognise, revoke it immediately and change your password.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <Loader2 size={24} className="animate-spin text-gray-300"/>
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No active sessions found</div>
        ) : (
          sessions.map((s) => {
            const { label, isMobile } = parseDevice(s.ua);
            const DeviceIcon = isMobile ? Smartphone : Monitor;
            return (
              <div key={s.sid} className={clsx(
                'flex items-center gap-4 px-5 py-4',
                s.isCurrent && 'bg-orange/5',
              )}>
                <div className={clsx(
                  'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
                  s.isCurrent ? 'bg-orange/10 text-orange' : 'bg-gray-100 text-gray-400',
                )}>
                  <DeviceIcon size={18}/>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-navy">{label}</span>
                    {s.isCurrent && (
                      <span className="text-2xs bg-orange/10 text-orange px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide">
                        This device
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5 truncate">
                    IP: {s.ip} · Signed in {fmtDate(s.createdAt)}
                  </div>
                  {s.lastActive && (
                    <div className="text-xs text-gray-300 truncate">
                      Last active {fmtDate(s.lastActive)}
                    </div>
                  )}
                </div>

                {!s.isCurrent && (
                  <button
                    onClick={() => revoke.mutate(s.sid)}
                    disabled={revoking === s.sid}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                               text-clinical-danger border border-red-100 hover:bg-red-50
                               transition-colors disabled:opacity-40 flex-shrink-0">
                    {revoking === s.sid
                      ? <Loader2 size={12} className="animate-spin"/>
                      : <Trash2 size={12}/>}
                    Revoke
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      <p className="text-xs text-gray-400">
        Signed in as <strong>{user?.name}</strong> ({user?.role?.replace(/_/g, ' ')})
      </p>
    </div>
  );
}
